import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, FileText, Sparkles, CheckCircle, AlertCircle, Folder, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotification } from '@/contexts/NotificationContext';
import { locationService, Location } from '@/firebase/locationService';
import { vendorService, Vendor } from '@/firebase/vendorService';
import { agreementService, AgreementDocument } from '@/firebase/agreementService';
import { agreementCompletionService } from '@/firebase/agreementCompletionService';
import { uploadAgreementDocument } from '@/firebase/agreementService';
import { parseContract, ContractAnalysis } from '@/services/geminiService';
import { extractVendorFromContract, VendorExtraction, classifyDocument, DocumentClassification, extractVendorNameFromFolder, extractContactFromMultipleFiles } from '@/services/geminiService';

interface BulkUploadVendorContractsProps {
  onClose: () => void;
  onComplete: () => void;
}

interface ProcessingFile {
  file: File;
  folderName?: string; // Folder name if file came from a folder
  status: 'pending' | 'processing' | 'classifying' | 'extracting-vendor' | 'extracting-contract' | 'creating-vendor' | 'uploading' | 'uploading-supplementary' | 'completed' | 'error';
  classification?: DocumentClassification;
  vendorExtraction?: VendorExtraction;
  contractAnalysis?: ContractAnalysis;
  vendorId?: string;
  error?: string;
  progress?: string;
}

interface FolderGroup {
  folderName: string;
  files: ProcessingFile[];
  vendorName?: string;
  vendorId?: string;
  mainContractFile?: ProcessingFile;
  supplementaryFiles: ProcessingFile[];
}

const BulkUploadVendorContracts: React.FC<BulkUploadVendorContractsProps> = ({ onClose, onComplete }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedAgreementId, setSelectedAgreementId] = useState<string>('');
  const [selectedAgreement, setSelectedAgreement] = useState<AgreementDocument | null>(null);
  const [files, setFiles] = useState<ProcessingFile[]>([]);
  const [folderGroups, setFolderGroups] = useState<Map<string, FolderGroup>>(new Map());
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStats, setProcessingStats] = useState({
    total: 0,
    completed: 0,
    errors: 0,
    vendorsCreated: 0,
    contractsUploaded: 0,
    supplementaryDocsUploaded: 0,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [existingVendors, setExistingVendors] = useState<Vendor[]>([]);
  const [existingAgreements, setExistingAgreements] = useState<AgreementDocument[]>([]);
  const processingResultsRef = useRef<{ completed: number; errors: number }>({ completed: 0, errors: 0 });

  React.useEffect(() => {
    loadLocations();
    loadExistingVendors();
    loadExistingAgreements();
  }, []);

  const loadLocations = async () => {
    try {
      let userLocationIds: string[] | undefined;
      let userRole: string | undefined;

      if (user?.id) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data() as any;
            userLocationIds = userData?.locationIds;
            userRole = userData?.role;
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }

      const result = await locationService.getAccessibleLocations(
        currentOrganization?.id,
        user?.id,
        userRole,
        userLocationIds
      );

      if (result.success && result.data) {
        const validLocations = result.data.filter(loc => loc.id && loc.name);
        setLocations(validLocations);
        if (validLocations.length === 1) {
          setSelectedLocationId(validLocations[0].id || '');
        }
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadExistingVendors = async () => {
    if (!currentOrganization?.id) return;
    try {
      const result = await vendorService.getAllVendors(currentOrganization.id);
      if (result.success && result.data) {
        setExistingVendors(result.data);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadExistingAgreements = async () => {
    if (!currentOrganization?.id) return;
    try {
      const result = await agreementService.getAllDocuments(currentOrganization.id);
      if (result.success && result.data) {
        // Filter for vendor contracts
        const vendorAgreements = result.data.filter(
          (agreement) => agreement.contractType === 'vendor_contract'
        );
        setExistingAgreements(vendorAgreements);
      }
    } catch (error) {
      console.error('Error loading agreements:', error);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = Array.from(e.dataTransfer.items);
    const files: File[] = [];
    let hasDirectories = false;
    let folderCount = 0;

    console.log(`[BulkUpload] Processing ${items.length} dropped item(s)`);

    // Process all items in parallel (both files and folders)
    const processPromises: Promise<File[]>[] = [];

    for (const item of items) {
      if (item.kind === 'file') {
        // Try to get as directory entry (Chrome/Edge support)
        const entry = (item as any).webkitGetAsEntry?.() || (item as any).getAsFileSystemHandle?.();
        
        if (entry) {
          // Check if it's a directory
          if (entry.isDirectory || entry.kind === 'directory') {
            hasDirectories = true;
            folderCount++;
            const folderName = entry.name || entry.fullPath || 'unknown';
            console.log(`[BulkUpload] Found folder: ${folderName}`);
            // Process directory in parallel
            processPromises.push(
              getFilesFromDirectory(entry, folderName).then((dirFiles) => {
                console.log(`[BulkUpload] Extracted ${dirFiles.length} files from folder: ${folderName}`);
                // Files already have __folderName attached by getFilesFromDirectory
                return dirFiles;
              }).catch((error) => {
                console.error('Error reading directory:', error);
                showNotification(`Error reading folder: ${folderName}. Skipping.`, 'error');
                return []; // Return empty array on error
              })
            );
          } else if (entry.isFile || entry.kind === 'file') {
            // It's a file entry - process in parallel
            processPromises.push(
              new Promise<File[]>((resolve) => {
                entry.file(
                  (file: File) => {
                    resolve([file]);
                  },
                  (error: any) => {
                    console.error('Error reading file:', error);
                    resolve([]);
                  }
                );
              })
            );
          }
        } else {
          // Fallback: try to get as file directly
          const file = item.getAsFile();
          if (file) {
            processPromises.push(Promise.resolve([file]));
          }
        }
      }
    }

    // Wait for all folders and files to be processed in parallel
    if (processPromises.length > 0) {
      try {
        const results = await Promise.all(processPromises);
        // Flatten all results into a single array
        results.forEach((fileArray) => {
          files.push(...fileArray);
        });
      } catch (error) {
        console.error('Error processing dropped items:', error);
        showNotification('Error processing dropped items. Some files may be missing.', 'error');
      }
    }

    // If we processed directories, use those files
    if (hasDirectories && files.length > 0) {
      const folderText = folderCount === 1 ? 'folder' : 'folders';
      showNotification(`Found ${files.length} file(s) in ${folderCount} ${folderText}`, 'success');
      handleFiles(files);
    } else if (files.length > 0) {
      // Regular files
      handleFiles(files);
    } else {
      // Fallback: try files API (for browsers that don't support directory API)
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        // Check if any of these files are actually from folders (they might have relative paths)
        const filesFromFolders = droppedFiles.filter(f => {
          // Files from folders often have path separators in their webkitRelativePath
          return (f as any).webkitRelativePath && (f as any).webkitRelativePath.includes('/');
        });
        
        if (filesFromFolders.length > 0) {
          const uniqueFolders = new Set(
            filesFromFolders.map(f => {
              const path = (f as any).webkitRelativePath || '';
              return path.split('/')[0]; // Get folder name
            })
          );
          const folderText = uniqueFolders.size === 1 ? 'folder' : 'folders';
          showNotification(`Found ${filesFromFolders.length} file(s) from ${uniqueFolders.size} ${folderText}`, 'success');
        }
        
        handleFiles(droppedFiles);
      } else {
        showNotification('No files found. Please ensure you are dropping files or folders. Note: Some browsers may require selecting folders via the file picker.', 'error');
      }
    }
  }, []);

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    // Check if we can access directory via File System Access API
    if (selectedFiles.length > 0 && 'webkitdirectory' in e.target) {
      // Files from directory input are already flattened
      handleFiles(selectedFiles);
    } else {
      handleFiles(selectedFiles);
    }
  };

  // Recursively get all files from a directory entry with folder name tracking
  const getFilesFromDirectory = async (directoryEntry: any, folderName?: string): Promise<File[]> => {
    const files: File[] = [];
    const currentFolderName = folderName || directoryEntry.name || 'Unknown Folder';
    
    return new Promise((resolve, reject) => {
      try {
        const reader = directoryEntry.createReader();
        
        const readEntries = () => {
          reader.readEntries(
            (entries: any[]) => {
              if (entries.length === 0) {
                resolve(files);
                return;
              }

              const promises: Promise<void>[] = [];

              entries.forEach((entry) => {
                if (entry.isDirectory) {
                  // Recursively process subdirectories (keep parent folder name)
                  promises.push(
                    getFilesFromDirectory(entry, currentFolderName).then((subFiles) => {
                      files.push(...subFiles);
                    }).catch((error) => {
                      console.error('Error reading subdirectory:', error);
                    })
                  );
                } else if (entry.isFile) {
                  // Get the file
                  promises.push(
                    new Promise<void>((resolveFile, rejectFile) => {
                      entry.file(
                        (file: File) => {
                          if (!file) {
                            console.warn('Received undefined file from directory entry');
                            resolveFile();
                            return;
                          }
                          // Accept all file types for supplementary documents
                          // Attach folder name to file for later processing
                          (file as any).__folderName = currentFolderName;
                          files.push(file);
                          resolveFile();
                        },
                        (error: any) => {
                          console.error('Error reading file:', error);
                          rejectFile(error);
                        }
                      );
                    })
                  );
                }
              });

              Promise.all(promises).then(() => {
                readEntries(); // Continue reading
              }).catch((error) => {
                console.error('Error processing entries:', error);
                readEntries(); // Continue even if some fail
              });
            },
            (error: any) => {
              console.error('Error reading directory entries:', error);
              reject(error);
            }
          );
        };

        readEntries();
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleFiles = (fileList: File[]) => {
    // Group files by folder using webkitRelativePath or __folderName if available
    const filesByFolder = new Map<string, ProcessingFile[]>();
    const standaloneFiles: ProcessingFile[] = [];

    fileList.forEach((file) => {
      if (!file) return; // Skip undefined/null files
      
      // Try to get folder name from multiple sources:
      // 1. __folderName (set when extracting from File System Access API)
      // 2. webkitRelativePath (from webkitdirectory input)
      const folderNameFromProperty = (file as any).__folderName;
      const relativePath = (file as any).webkitRelativePath;
      let folderName: string | undefined;
      
      if (folderNameFromProperty) {
        folderName = folderNameFromProperty;
      } else if (relativePath && relativePath.includes('/')) {
        folderName = relativePath.split('/')[0];
      }

      const processingFile: ProcessingFile = {
        file,
        folderName,
        status: 'pending',
      };

      if (folderName) {
        if (!filesByFolder.has(folderName)) {
          filesByFolder.set(folderName, []);
        }
        filesByFolder.get(folderName)!.push(processingFile);
      } else {
        standaloneFiles.push(processingFile);
      }
    });

    // Create folder groups
    const newFolderGroups = new Map<string, FolderGroup>();
    
    filesByFolder.forEach((files, folderName) => {
      const vendorName = extractVendorNameFromFolder(folderName);
      newFolderGroups.set(folderName, {
        folderName,
        files,
        vendorName,
        supplementaryFiles: [],
      });
    });

    // Add standalone files as individual "folders"
    standaloneFiles.forEach((file) => {
      const folderName = `Standalone: ${file.file.name}`;
      newFolderGroups.set(folderName, {
        folderName,
        files: [file],
        supplementaryFiles: [],
      });
    });

    setFolderGroups((prev) => {
      const merged = new Map(prev);
      newFolderGroups.forEach((group, key) => {
        if (merged.has(key)) {
          // Merge files if folder already exists
          const existing = merged.get(key)!;
          existing.files.push(...group.files);
        } else {
          merged.set(key, group);
        }
      });
      return merged;
    });

    // Also update files list for backward compatibility
    const allFiles = Array.from(newFolderGroups.values()).flatMap(g => g.files);
    setFiles((prev) => [...prev, ...allFiles]);

    const folderCount = filesByFolder.size;
    if (folderCount > 0) {
      showNotification(`Processed ${fileList.length} file(s) from ${folderCount} folder(s)`, 'success');
    } else {
      showNotification(`Added ${fileList.length} file(s)`, 'success');
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const findOrCreateVendor = async (vendorExtraction: VendorExtraction): Promise<string | null> => {
    if (!currentOrganization?.id) return null;

    // Try to find existing vendor by name (case-insensitive)
    const existingVendor = existingVendors.find(
      (v) => v.name.toLowerCase().trim() === vendorExtraction.vendorName.toLowerCase().trim()
    );

    if (existingVendor?.id) {
      return existingVendor.id;
    }

      // Create new vendor
      try {
        const vendorData: Omit<Vendor, 'id'> = {
          name: vendorExtraction.vendorName,
          contact: vendorExtraction.contact,
          email: vendorExtraction.email,
          phone: vendorExtraction.phone,
          category: vendorExtraction.category,
          locationIds: selectedLocationId ? [selectedLocationId] : undefined,
          organizationId: currentOrganization.id,
        };

        const result = await vendorService.addVendor(vendorData, currentOrganization.id);
        if (result.success && result.id) {
          // Add to existing vendors list
          setExistingVendors((prev) => [...prev, { ...vendorData, id: result.id } as Vendor]);
          return result.id;
        }
      } catch (error: any) {
        console.error('Error creating vendor:', error);
        throw error;
      }

    return null;
  };

  // Get the selected agreement - must be selected by user
  const getSelectedAgreement = (): AgreementDocument | null => {
    if (!selectedAgreementId) return null;
    return existingAgreements.find(a => a.id === selectedAgreementId) || selectedAgreement;
  };

  // Process a folder group (all files in a folder)
  const processFolder = async (folderName: string, group: FolderGroup): Promise<void> => {
    if (!currentOrganization?.id || !selectedLocationId || !selectedAgreementId) {
      return;
    }

    const updateGroupStatus = (updates: Partial<FolderGroup>) => {
      setFolderGroups((prev) => {
        const updated = new Map(prev);
        const existing = updated.get(folderName);
        if (existing) {
          updated.set(folderName, { ...existing, ...updates });
        }
        return updated;
      });
    };

    const updateFileInGroup = (fileIndex: number, updates: Partial<ProcessingFile>) => {
      setFolderGroups((prev) => {
        const updated = new Map(prev);
        const existing = updated.get(folderName);
        if (existing) {
          const newFiles = [...existing.files];
          if (newFiles[fileIndex]) {
            newFiles[fileIndex] = { ...newFiles[fileIndex], ...updates };
          }
          updated.set(folderName, { ...existing, files: newFiles });
        }
        return updated;
      });
    };

    try {
      // Step 1: Classify all files in the folder
      updateGroupStatus({});
      for (let i = 0; i < group.files.length; i++) {
        updateFileInGroup(i, { status: 'classifying', progress: 'Classifying document...' });
        try {
          const classification = await classifyDocument(group.files[i].file, folderName);
          updateFileInGroup(i, { classification, status: 'pending' });
        } catch (error: any) {
          console.error(`Error classifying ${group.files[i].file.name}:`, error);
          // Continue with default classification
          updateFileInGroup(i, { 
            classification: { documentType: 'other', confidence: 'low', isMainContract: false },
            status: 'pending'
          });
        }
      }

      // Step 2: Identify main contract (highest priority: isMainContract=true, then contract type, then first PDF/Word)
      const classifiedFiles = group.files.map((f, idx) => ({ file: f, index: idx }));
      const mainContract = classifiedFiles.find(f => f.file.classification?.isMainContract) ||
                          classifiedFiles.find(f => f.file.classification?.documentType === 'contract') ||
                          classifiedFiles.find(f => {
                            const name = f.file.file.name.toLowerCase();
                            return name.includes('contract') || name.includes('agreement');
                          }) ||
                          classifiedFiles.find(f => {
                            const type = f.file.file.type;
                            return type === 'application/pdf' || 
                                   type === 'application/msword' || 
                                   type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                          }) ||
                          classifiedFiles[0]; // Fallback to first file

      if (!mainContract) {
        throw new Error('No contract file found in folder');
      }

      updateFileInGroup(mainContract.index, { status: 'extracting-vendor', progress: 'Extracting vendor information...' });
      updateGroupStatus({ mainContractFile: mainContract.file });

      // Step 3: Extract vendor name from folder name (primary) or contract (fallback)
      let vendorName = group.vendorName || extractVendorNameFromFolder(folderName);
      let vendorExtraction: VendorExtraction;

      try {
        vendorExtraction = await extractVendorFromContract(mainContract.file.file);
        // Use folder name if AI extraction doesn't provide a good vendor name
        if (!vendorExtraction.vendorName || vendorExtraction.vendorName.toLowerCase().includes('unknown')) {
          vendorExtraction.vendorName = vendorName;
        } else {
          vendorName = vendorExtraction.vendorName;
        }
      } catch (error) {
        // Fallback to folder name
        vendorExtraction = {
          vendorName: vendorName,
        };
      }

      // Step 4: Extract contact information from all files
      updateFileInGroup(mainContract.index, { status: 'extracting-contract', progress: 'Extracting contact information...' });
      const allFiles = group.files.map(f => f.file);
      const contactInfo = await extractContactFromMultipleFiles(allFiles);
      
      // Merge contact info into vendor extraction
      if (contactInfo.contact && !vendorExtraction.contact) {
        vendorExtraction.contact = contactInfo.contact;
      }
      if (contactInfo.email && !vendorExtraction.email) {
        vendorExtraction.email = contactInfo.email;
      }
      if (contactInfo.phone && !vendorExtraction.phone) {
        vendorExtraction.phone = contactInfo.phone;
      }

      // Step 5: Find or create vendor
      updateFileInGroup(mainContract.index, { status: 'creating-vendor', progress: 'Creating/updating vendor profile...' });
      const vendorId = await findOrCreateVendor(vendorExtraction);
      if (!vendorId) {
        throw new Error('Failed to create or find vendor');
      }

      const wasNewVendor = !existingVendors.find(v => v.id === vendorId);
      if (wasNewVendor) {
        setProcessingStats((prev) => ({ ...prev, vendorsCreated: prev.vendorsCreated + 1 }));
        // Reload vendors to get the new one
        await loadExistingVendors();
      }

      updateGroupStatus({ vendorId, vendorName });
      updateFileInGroup(mainContract.index, { vendorId, vendorExtraction });

      // Step 6: Process main contract
      updateFileInGroup(mainContract.index, { status: 'extracting-contract', progress: 'Analyzing contract...' });
      const contractAnalysis = await parseContract(mainContract.file.file);
      updateFileInGroup(mainContract.index, { contractAnalysis, status: 'uploading', progress: 'Uploading contract...' });

      const agreement = getSelectedAgreement();
      if (!agreement?.id) {
        throw new Error('No agreement selected');
      }

      const signedPdfUrl = await uploadAgreementDocument(mainContract.file.file, {
        docType: 'agreement_completion',
        organizationId: currentOrganization.id,
        locationId: selectedLocationId,
        agreementId: agreement.id,
        uploadedBy: user ? { id: user.id, email: user.email, name: user.name } : undefined,
        title: `${vendorName} - Contract`,
        source: 'web',
        additionalMetadata: {
          signerName: vendorName,
        },
      });

      // Step 7: Create completion record
      const normalizeKeyInformation = (keyInfo: Record<string, any> | undefined): Record<string, string> => {
        if (!keyInfo) return {};
        const normalized: Record<string, string> = {};
        for (const [key, value] of Object.entries(keyInfo)) {
          if (value === null || value === undefined) {
            normalized[key] = '';
          } else if (typeof value === 'object') {
            try {
              normalized[key] = JSON.stringify(value);
            } catch (e) {
              normalized[key] = String(value);
            }
          } else {
            normalized[key] = String(value);
          }
        }
        return normalized;
      };

      const completionData: any = {
        agreementId: agreement.id,
        agreementName: agreement.name || 'Vendor Contract',
        signerName: vendorName,
        signerEmail: undefined,
        signatureDataUrl: '',
        signedPdfUrl: signedPdfUrl,
        organizationId: currentOrganization.id,
        locationId: selectedLocationId,
        signedDate: new Date(),
        vendorId: vendorId,
        vendorName: vendorName,
        contractSummary: contractAnalysis?.summary,
        contractDate: contractAnalysis?.contractDate ? contractAnalysis.contractDate.toISOString() : undefined,
        expiryDate: contractAnalysis?.expiryDate ? contractAnalysis.expiryDate.toISOString() : undefined,
        contractKeyInformation: normalizeKeyInformation(contractAnalysis?.keyInformation),
      };

      const result = await agreementCompletionService.addCompletion(completionData);
      if (!result.success) {
        throw new Error(result.error || 'Failed to save completion');
      }

      updateFileInGroup(mainContract.index, { status: 'completed', progress: 'Contract uploaded successfully' });
      setProcessingStats((prev) => ({
        ...prev,
        completed: prev.completed + 1,
        contractsUploaded: prev.contractsUploaded + 1,
      }));

      // Step 8: Upload supplementary documents
      const supplementaryFiles = group.files.filter((f, idx) => idx !== mainContract.index);
      if (supplementaryFiles.length > 0) {
        updateFileInGroup(mainContract.index, { status: 'uploading-supplementary', progress: `Uploading ${supplementaryFiles.length} supplementary document(s)...` });
        
        for (let i = 0; i < supplementaryFiles.length; i++) {
          const suppFile = supplementaryFiles[i];
          const suppIndex = group.files.findIndex(f => f.file.name === suppFile.file.name);
          
          if (suppIndex >= 0) {
            try {
              updateFileInGroup(suppIndex, { status: 'uploading-supplementary', progress: 'Uploading...' });
              
              const docType = suppFile.classification?.documentType || 'other';
              const uploadResult = await vendorService.uploadSupplementaryDocument(
                vendorId,
                suppFile.file,
                docType === 'communication' ? 'communication' :
                docType === 'amendment' ? 'amendment' :
                docType === 'certificate' ? 'certificate' :
                docType === 'license' ? 'license' : 'other',
                user ? { id: user.id, email: user.email, name: user.name } : undefined
              );

              if (uploadResult.success) {
                updateFileInGroup(suppIndex, { status: 'completed', progress: 'Uploaded successfully' });
                setProcessingStats((prev) => ({
                  ...prev,
                  supplementaryDocsUploaded: prev.supplementaryDocsUploaded + 1,
                }));
              } else {
                throw new Error(uploadResult.error || 'Upload failed');
              }
            } catch (error: any) {
              console.error(`Error uploading supplementary document ${suppFile.file.name}:`, error);
              updateFileInGroup(suppIndex, { 
                status: 'error', 
                error: error.message || 'Upload failed',
                progress: 'Error occurred'
              });
            }
          }
        }
      }

      processingResultsRef.current.completed++;
    } catch (error: any) {
      console.error(`Error processing folder ${folderName}:`, error);
      // Mark all files in folder as error
      group.files.forEach((_, idx) => {
        updateFileInGroup(idx, {
          status: 'error',
          error: error.message || 'Unknown error',
          progress: 'Error occurred',
        });
      });
      processingResultsRef.current.errors++;
      setProcessingStats((prev) => ({
        ...prev,
        errors: prev.errors + 1,
      }));
    }
  };

  const processFile = async (fileIndex: number): Promise<void> => {
    const processingFile = files[fileIndex];
    if (!processingFile || !currentOrganization?.id || !selectedLocationId || !selectedAgreementId) {
      return;
    }

    const updateFileStatus = (updates: Partial<ProcessingFile>) => {
      setFiles((prev) =>
        prev.map((f, i) => (i === fileIndex ? { ...f, ...updates } : f))
      );
    };

    try {
      // Step 1: Extract vendor information
      updateFileStatus({ status: 'extracting-vendor', progress: 'Extracting vendor information...' });
      const vendorExtraction = await extractVendorFromContract(processingFile.file);
      updateFileStatus({ vendorExtraction, status: 'extracting-contract', progress: 'Analyzing contract...' });

      // Step 2: Extract contract information
      const contractAnalysis = await parseContract(processingFile.file);
      updateFileStatus({ contractAnalysis, status: 'creating-vendor', progress: 'Creating vendor profile...' });

      // Step 3: Find or create vendor
      const vendorId = await findOrCreateVendor(vendorExtraction);
      if (!vendorId) {
        throw new Error('Failed to create or find vendor');
      }
      
      // Check if vendor was just created (not in existing vendors list)
      const wasNewVendor = !existingVendors.find(v => v.id === vendorId);
      if (wasNewVendor) {
        setProcessingStats((prev) => ({ ...prev, vendorsCreated: prev.vendorsCreated + 1 }));
      }
      
      updateFileStatus({ vendorId, status: 'uploading', progress: 'Uploading contract...' });

      // Step 4: Use the selected agreement (same for all contracts)
      const agreement = getSelectedAgreement();
      if (!agreement?.id) {
        throw new Error('No agreement selected');
      }
      const agreementId = agreement.id;
      const agreementName = agreement.name || 'Vendor Contract';

      // Step 5: Upload contract document
      const signedPdfUrl = await uploadAgreementDocument(processingFile.file, {
        docType: 'agreement_completion',
        organizationId: currentOrganization.id,
        locationId: selectedLocationId,
        agreementId: agreementId,
        uploadedBy: user
          ? { id: user.id, email: user.email, name: user.name }
          : undefined,
        title: `${vendorExtraction.vendorName} - Contract`,
        source: 'web',
        additionalMetadata: {
          signerName: vendorExtraction.vendorName,
        },
      });

      // Step 6: Create completion record
      const normalizeKeyInformation = (keyInfo: Record<string, any> | undefined): Record<string, string> => {
        if (!keyInfo) return {};
        const normalized: Record<string, string> = {};
        for (const [key, value] of Object.entries(keyInfo)) {
          if (value === null || value === undefined) {
            normalized[key] = '';
          } else if (typeof value === 'object') {
            try {
              normalized[key] = JSON.stringify(value);
            } catch (e) {
              normalized[key] = String(value);
            }
          } else {
            normalized[key] = String(value);
          }
        }
        return normalized;
      };

      const completionData: any = {
        agreementId: agreementId,
        agreementName: agreementName,
        signerName: vendorExtraction.vendorName,
        signerEmail: undefined,
        signatureDataUrl: '',
        signedPdfUrl: signedPdfUrl,
        organizationId: currentOrganization.id,
        locationId: selectedLocationId,
        signedDate: new Date(),
        vendorId: vendorId,
        vendorName: vendorExtraction.vendorName,
        contractSummary: contractAnalysis?.summary,
        contractDate: contractAnalysis?.contractDate ? contractAnalysis.contractDate.toISOString() : undefined,
        expiryDate: contractAnalysis?.expiryDate ? contractAnalysis.expiryDate.toISOString() : undefined,
        contractKeyInformation: normalizeKeyInformation(contractAnalysis?.keyInformation),
      };

      const result = await agreementCompletionService.addCompletion(completionData);

      if (result.success) {
        updateFileStatus({
          status: 'completed',
          progress: 'Completed successfully',
        });
        processingResultsRef.current.completed++;
        setProcessingStats((prev) => ({
          ...prev,
          completed: prev.completed + 1,
          contractsUploaded: prev.contractsUploaded + 1,
        }));
      } else {
        throw new Error(result.error || 'Failed to save completion');
      }
    } catch (error: any) {
      console.error(`Error processing file ${processingFile.file.name}:`, error);
      updateFileStatus({
        status: 'error',
        error: error.message || 'Unknown error',
        progress: 'Error occurred',
      });
      processingResultsRef.current.errors++;
      setProcessingStats((prev) => ({
        ...prev,
        errors: prev.errors + 1,
      }));
    }
  };

  const processAllFiles = async () => {
    if (!selectedLocationId || !selectedAgreementId) {
      showNotification('Please select location and agreement', 'error');
      return;
    }

    if (folderGroups.size === 0 && files.length === 0) {
      showNotification('Please add files or folders to upload', 'error');
      return;
    }

    setIsProcessing(true);
    // Reset the ref tracking
    processingResultsRef.current = { completed: 0, errors: 0 };
    const totalFolders = folderGroups.size || (files.length > 0 ? 1 : 0);
    setProcessingStats({
      total: totalFolders,
      completed: 0,
      errors: 0,
      vendorsCreated: 0,
      contractsUploaded: 0,
      supplementaryDocsUploaded: 0,
    });

    // Process folders first (if any)
    if (folderGroups.size > 0) {
      for (const [folderName, group] of folderGroups.entries()) {
        await processFolder(folderName, group);
        // Small delay between folders to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    } else if (files.length > 0) {
      // Fallback: process individual files (backward compatibility)
      for (let i = 0; i < files.length; i++) {
        await processFile(i);
        if (i < files.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    }

    // Wait a moment for final state updates
    await new Promise((resolve) => setTimeout(resolve, 500));

    setIsProcessing(false);
    
    // Use the ref which tracks results accurately
    const finalCompleted = processingResultsRef.current.completed;
    const finalErrors = processingResultsRef.current.errors;
    const stats = processingStats;

    console.log('[BulkUpload] Final stats:', { finalCompleted, finalErrors, stats });

    if (finalCompleted > 0) {
      const message = `Successfully processed ${finalCompleted} folder(s). ${stats.contractsUploaded} contract(s) and ${stats.supplementaryDocsUploaded} supplementary document(s) uploaded. ${finalErrors > 0 ? `${finalErrors} error(s).` : ''}`;
      showNotification(
        message,
        finalCompleted === totalFolders && finalErrors === 0 ? 'success' : 'info'
      );
      onComplete();
    } else {
      showNotification('Failed to process folders. Please check errors and try again.', 'error');
    }
  };

  const getStatusIcon = (status: ProcessingFile['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'processing':
      case 'extracting-vendor':
      case 'extracting-contract':
      case 'creating-vendor':
      case 'uploading':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: ProcessingFile['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'processing':
      case 'extracting-vendor':
      case 'extracting-contract':
      case 'creating-vendor':
      case 'uploading':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Bulk Upload Vendor Contracts
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Upload multiple contracts. AI will extract vendor details and create profiles automatically.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1"
            disabled={isProcessing}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Selection Fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location <span className="text-red-600">*</span>
                </label>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                  disabled={isProcessing}
                >
                  <option value="">Select location...</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Agreement <span className="text-red-600">*</span>
                </label>
                <select
                  value={selectedAgreementId}
                  onChange={(e) => {
                    const agreementId = e.target.value;
                    setSelectedAgreementId(agreementId);
                    const agreement = existingAgreements.find(a => a.id === agreementId);
                    setSelectedAgreement(agreement || null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                  disabled={isProcessing}
                >
                  <option value="">Select agreement...</option>
                  {existingAgreements.length === 0 ? (
                    <option value="" disabled>No vendor contract agreements found. Please create one first.</option>
                  ) : (
                    existingAgreements.map((agreement) => (
                      <option key={agreement.id} value={agreement.id}>
                        {agreement.name}
                      </option>
                    ))
                  )}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  All contracts will be uploaded under the selected agreement.
                </p>
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Contracts <span className="text-red-600">*</span>
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => !isProcessing && fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-sm text-gray-600 mb-2">
                {isDragging ? 'Drop files here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-gray-500">
                PDF or Word documents (.pdf, .doc, .docx)
              </p>
              <p className="text-xs text-gray-400 mt-2">
                You can drag and drop multiple folders or files at once
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Folders will be scanned recursively for contract files
              </p>
              <p className="text-xs text-blue-600 mt-1 font-medium">
                💡 Tip: Select multiple folders (Ctrl+Click) then drag them all together
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileInput}
                className="hidden"
                disabled={isProcessing}
              />
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Files to Process ({files.length})
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className={`p-3 border rounded-md ${getStatusColor(file.status)} flex items-center justify-between`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {getStatusIcon(file.status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.file.name}
                        </p>
                        {file.progress && (
                          <p className="text-xs text-gray-600 mt-1">{file.progress}</p>
                        )}
                        {file.error && (
                          <p className="text-xs text-red-600 mt-1">{file.error}</p>
                        )}
                        {file.vendorExtraction && (
                          <p className="text-xs text-gray-500 mt-1">
                            Vendor: {file.vendorExtraction.vendorName}
                          </p>
                        )}
                      </div>
                    </div>
                    {!isProcessing && (
                      <button
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-600 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Processing Stats */}
          {isProcessing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <h4 className="text-sm font-medium text-blue-900">Processing Contracts...</h4>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Total:</span>{' '}
                  <span className="font-semibold">{processingStats.total}</span>
                </div>
                <div>
                  <span className="text-green-700">Completed:</span>{' '}
                  <span className="font-semibold">{processingStats.completed}</span>
                </div>
                <div>
                  <span className="text-purple-700">Vendors Created:</span>{' '}
                  <span className="font-semibold">{processingStats.vendorsCreated}</span>
                </div>
                <div>
                  <span className="text-blue-700">Contracts:</span>{' '}
                  <span className="font-semibold">{processingStats.contractsUploaded}</span>
                </div>
                <div>
                  <span className="text-green-700">Supplementary Docs:</span>{' '}
                  <span className="font-semibold">{processingStats.supplementaryDocsUploaded}</span>
                </div>
                <div>
                  <span className="text-red-700">Errors:</span>{' '}
                  <span className="font-semibold">{processingStats.errors}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            onClick={processAllFiles}
            disabled={isProcessing || !selectedLocationId || !selectedAgreementId || (folderGroups.size === 0 && files.length === 0)}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Process {folderGroups.size > 0 ? `${folderGroups.size} Folder${folderGroups.size !== 1 ? 's' : ''}` : `${files.length} File${files.length !== 1 ? 's' : ''}`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadVendorContracts;

